/* eslint-disable sonarjs/cognitive-complexity */
import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from 'context/AuthContext';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { useSignin } from 'hooks/useSignin';
import { useForm } from 'hooks/useForm';
import { Link as GatsbyLink, navigate } from 'gatsby';
import { Link } from 'components/Link/Link';
import { FormButton } from 'components/forms/FormButton';
import { asButton } from '@getstream/ui-library/build/Button';
import { InputField } from 'components/forms/InputField';
import { FormSeparator } from 'components/forms/FormSeparator';
import { IconChevronRight } from 'components/Icon/IconChevronRight';
import { onGithubLoginClick } from 'utils/auth';
import { LoadingSpinner } from 'components/LoadingSpinner/LoadingSpinner';
import { NoSSR } from 'components/NoSSR/NoSSR';
import { config } from 'utils/config';
import { urls, isValidHost, isPathInKnownUrls } from 'utils/urls';
import { GoogleSignup } from 'components/GoogleSignin';

import * as styles from './index.module.css';

const Button = asButton((props) => <GatsbyLink {...props} />);
const BaseButton = asButton((props) => <button {...props} />);

export const SignInForm = ({ label, redirect, isCaptcha }) => {
  const userLabel = 'Organization Name';
  const { submitHandler, loading, error } = useSignin(redirect);

  const { isLoggedIn, getUserData, getSelectedOrg } = useContext(AuthContext);
  const [email, setEmail] = useState();
  const [redirectLocation, setRedirectLocation] = useState(urls.dashboard);
  const [org, setOrg] = useState(getSelectedOrg());

  const {
    submitHandler: submitOrgHandler,
    loading: orgLoading,
    error: orgError,
    clearError: clearOrgError,
  } = useForm('org', () => {
    navigate(redirectLocation);
  });

  useEffect(() => {
    if (isLoggedIn) {
      if (!email) {
        (async () => {
          const user = await getUserData();
          if (user && user.email) {
            setEmail(user.email);
          } else {
            // eslint-disable-next-line no-console
            console.log('No user found at signin');
          }
        })();
      }
      if (org) {
        // eslint-disable-next-line no-console
        console.log('Org found at signin', org);
        if (redirect) {
          const urlParams = new URLSearchParams(window.location.search);
          const nextProp = redirect;
          const nextParam = urlParams.get('next');
          try {
            const url = nextParam && new URL(nextParam);
            const navigateTo = (isValidHost(url?.host) && nextParam) || (isPathInKnownUrls(nextProp, urls) && nextProp) || redirectLocation;
            navigate(navigateTo);
          } catch (e) {
            // eslint-disable-next-line no-console
            console.warn('Error parsing URL from next param', e);
          }
        } else {
          navigate(redirectLocation);
        }
      } else {
        // eslint-disable-next-line no-console
        console.log('No org found at signin');
        setRedirectLocation(urls.onboarding);
        (async () => {
          const myOrg = await getSelectedOrg();
          setOrg(myOrg);
        })();
      }
    }
  }, [getSelectedOrg, isLoggedIn, org, redirect, getUserData, redirectLocation, email]);

  const SubmitButton = ({ disabled }) => (
    <FormButton color='blue' icon='right' type='submit' id='loginButton' disabled={disabled}>
      <span>{loading ? <LoadingSpinner /> : label}</span>
      <IconChevronRight />
    </FormButton>
  );

  /* The Bizible-Exclude class avoids this form from being tracked by Bizible (3rd party tracking tool) */
  return (
    <>
      {!isLoggedIn ? (
        <form onSubmit={submitHandler} method='POST' className='Bizible-Exclude'>
          <InputField name='username' id='username' label='username or email:' type='text' placeholder='Username or Email' required />
          <div className={styles.password}>
            <InputField label='password:' name='password' id='password' placeholder='******' type='password' required />
            <Link to={urls.accounts.password_reset} customClass={styles.passwordForgotLink} emphasis strong>
              Forgot Password?
            </Link>
          </div>
          {error && <p className={styles.errorMessage}>{error}</p>}

          <NoSSR Fallback={<SubmitButton />}>
            {isCaptcha && (
              <div className={cn(styles.recaptcha, 'g-recaptcha')} data-sitekey={config.recaptchaSiteKey} data-callback='onCaptcha'></div>
            )}
            <SubmitButton disabled={isCaptcha} />
          </NoSSR>
          <div className={styles.socialLogin}>
            <FormSeparator />
            <div className={styles.socialLoginButtons}>
              <div className={styles.socialButtonWrapper}>
                <GoogleSignup />
                <BaseButton color='secondary' leftIcon='github' onClick={onGithubLoginClick} className={styles.customSocialButton}>
                  Continue with GitHub
                </BaseButton>
              </div>
              <Button color='secondary' shadow='default' to={urls.accounts.login_sso}>
                Sign In with SSO
              </Button>
            </div>
          </div>
        </form>
      ) : (
        !org && (
          <div>
            <form onSubmit={submitOrgHandler}>
              <p className={styles.orgDescription}>
                {'Create a new organization for "'}
                <strong>{email}</strong>
                {'"'}
              </p>
              <InputField
                name='username'
                id='username'
                label={userLabel}
                placeholder={userLabel.toLowerCase().replace(' ', '-')}
                error={error && error.username}
                onChange={() => clearOrgError('username')}
                required
                help='This value may contain only letters, numbers, and @/./+/-/_ characters.'
              />
              <InputField name='gotcha' id='gotcha' hidden />

              {orgError && (orgError.detail || orgError.non_field_errors) && (
                <div className={styles.errorMessage}>{orgError.detail || orgError.non_field_errors}</div>
              )}

              <BaseButton color='primary' rightIcon='chevron' shadow='default' className={styles.button}>
                {orgLoading ? <LoadingSpinner /> : 'Next'}
              </BaseButton>
            </form>
          </div>
        )
      )}
    </>
  );
};

SignInForm.propTypes = {
  label: PropTypes.string,
  redirect: PropTypes.string,
};
